<template>
  <iframe
    v-if="googleMapUrl && type === 'google'"
    width="100%"
    height="244"
    class="map-infrastructure"
    style="border: 0"
    loading="lazy"
    allowfullscreen
    referrerpolicy="no-referrer-when-downgrade"
    :src="googleMapUrl"
  />
  <div
    v-show="showMaps"
    v-if="version === 'print' && type === 'yandex'"
    class="d-flex justify-content-between nobreak"
  >
    <div class="text-center">
      <div v-if="isInfrastructure">
        <div
          ref="mapInfrastructure"
          class="map-infrastructure"
          :id="leadId"
          width="100%"
          height="244"
          frameborder="0"
        />
        <br />
      </div>
      <div v-else-if="address">
        <div
          ref="printMap"
          class="print_map"
          :id="leadId"
          width="100%"
          height="244"
          frameborder="0"
        />
        <br />
      </div>
    </div>
  </div>
  <div
    v-show="showMaps"
    v-if="version === 'standard' && type === 'yandex'"
    class="map-wrapper"
  >
    <div ref="mapInfrastructure" :id="leadId" class="map-infrastructure" />
    <div
      v-if="isInfrastructure"
      :id="`panorama_${leadId}`"
      class="map-infrastructure map-panorama"
    />
  </div>
</template>

<script>
import $ from 'jquery'
import ymaps from 'ymaps'
import { mapState } from 'pinia'
import { useCommonStore } from '@/stores/common'
import { ref } from '@vue/reactivity'
import api from '../../../services/api.js'
import Locale from '../../../services/locale'
import { metrikaEvents } from '../../../services/metrika'

export default {
  name: 'Maps',
  props: {
    type: String,
    version: String,
    isInfrastructure: Boolean,
    address: String,
    leadId: Number,
    yakey: String,
    longitude: String,
    latitude: String,
    localeService: Locale,
  },
  computed: {
    ...mapState(useCommonStore, ['activeLang']),
    panorama() {
      return this.localeService.getConstantTranslationByKey('panorama')
    },
    googleMapUrl() {
      const withCoordinates = this.latitude && this.longitude
      if (!(withCoordinates || this.address)) return

      const url = new URL('https://www.google.com/maps/embed/v1/place')
      url.searchParams.append('key', 'AIzaSyBRRZQPj6rxinZWGq9TO-2b7Cke8Z1PdZw')
      if (this.activeLang) {
        url.searchParams.append('language', this.activeLang)
      }
      const query = withCoordinates
        ? `${this.latitude},${this.longitude}`
        : this.address
      url.searchParams.append('q', query)
      return url.toString()
    },
  },
  methods: {
    getAddressMarker() {
      const ymaps = this.yandexMaps
      const markerWhenCoordinates =
        this.latitude &&
        this.longitude &&
        new ymaps.GeoObject({
          geometry: new ymaps.geometry.Point([
            Number(this.latitude),
            Number(this.longitude),
          ]),
        })

      return (
        markerWhenCoordinates ||
        (this.address &&
          ymaps
            .geocode(this.address, { results: 1 })
            .then((res) => res.geoObjects))
      )
    },
    openStandardMapPanorama(coords, id, panoramaWrapper) {
      const ymaps = this.yandexMaps
      if (!ymaps.panorama.isSupported()) {
        return
      }
      ymaps.panorama.locate(coords).done((panoramas) => {
        if (!panoramas.length) {
          panoramaWrapper.classList.remove('panorama-size')
          return
        }
        const panorama = new ymaps.panorama.Player(
          `panorama_${id}`,
          panoramas[0],
        )
        panorama.events.add('destroy', () =>
          panoramaWrapper.classList.remove('panorama-size'),
        )
      })
    },
    async initMapWithInfrastructure() {
      const ymaps = this.yandexMaps
      const map = this.$refs.mapInfrastructure
      const mapId = map.getAttribute('id')
      const mapInstance = new ymaps.Map(mapId, {
        center: [55.753994, 37.622093],
        zoom: 16,
        controls: ['zoomControl'],
      })
      const objectsCollection = new ymaps.GeoObjectCollection()

      $('ymaps.ymaps-2-1-78-map-copyrights-promo').remove()

      const addressMarker = await this.getAddressMarker()
      if (addressMarker) {
        objectsCollection.add(addressMarker)
        mapInstance.geoObjects.add(objectsCollection)
      } else {
        this.showMaps = false
        return
      }

      const objectsCenter = ymaps.util.bounds.getCenterAndZoom(
        objectsCollection.getBounds(),
        mapInstance.container.getSize(),
      ).center
      mapInstance.setCenter(objectsCenter, 15)

      mapInstance.events.add('click', () => {
        metrikaEvents.onTapOnMap()
      })
      mapInstance.events.add('boundschange', (e) => {
        const to = e.get('newZoom')
        const from = e.get('oldZoom')
        if (to === from) return
        metrikaEvents.onMapZoom(from, to)
      })

      if (this.version === 'standard' && this.isInfrastructure) {
        const btnId = `btnToOpenPanorama_${mapId}`
        const button = new ymaps.control.Button({
          data: {
            title: this.panorama,
            id: btnId,
          },
          options: {
            layout: ymaps.templateLayoutFactory.createClass(`
              <div id="{{ data.id }}" class="panorama-wrapper" title="{{ data.title }}">
                <span class="panorama-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd"
                          d="M13.379 17.24l-.323-3.232a.09.09 0 0 0-.178-.01l-.598 2.692-.831-.416a.45.45 0 0 1-.249-.402v-3.586a.45.45 0 0 0-.132-.318l-.636-.636a.45.45 0 0 1-.132-.318V8.849a.9.9 0 0 1 1.185-.854l3.292 1.097c.551.184.923.7.923 1.281v2.349a.45.45 0 0 1-.249.402l-.402.202a.45.45 0 0 0-.249.402v3.785a.27.27 0 0 1-.39.242l-1.031-.516zM9.4 14.657c-2.208.41-3.6 1.174-3.6 1.942 0 .77 1.468 1.588 3.877 1.992l.118-1.372a.195.195 0 0 1 .023-.076c.05-.09.161-.12.248-.068l4.194 2.716c.033.02.06.052.075.09a.193.193 0 0 1-.095.248l-4.588 1.855a.174.174 0 0 1-.089.014.187.187 0 0 1-.162-.207l.12-1.399C6.195 19.871 4 18.58 4 16.6c0-1.951 2.165-3.249 5.4-3.78v1.838zm7.2 0V12.82c3.237.532 5.4 1.833 5.4 3.792 0 1.934-2.062 3.186-5.256 3.738a.9.9 0 0 1-.307-1.773c2.414-.418 3.763-1.237 3.763-1.965 0-.777-1.39-1.544-3.6-1.955zM13 7.6A1.8 1.8 0 1 1 13 4a1.8 1.8 0 0 1 0 3.6z"></path>
                  </svg>
                </span>
                <span class="panorama-title">{{ data.title }}</span>
              </div>
            `),
            maxWidth: 132,
          },
        })

        mapInstance.controls.add(button, { float: 'right' })
        setTimeout(() => {
          const targetElement = document.getElementById(btnId)
          const panoramaWrapper = document.getElementById(`panorama_${mapId}`)
          ymaps.domEvent.manager.group(targetElement).add('click', () => {
            panoramaWrapper.classList.add('panorama-size')
            this.openStandardMapPanorama(objectsCenter, mapId, panoramaWrapper)
          })
        }, 0)
      }

      if (!this.isInfrastructure) {
        return
      }

      const mapInfrastructure = await api.getMapInfrastructure(
        mapInstance.getBounds(),
      )
      if (!mapInfrastructure) {
        return
      }
      mapInfrastructure.pois.forEach((infrasItem) => {
        infrasItem.pointOfInterest.forEach((properties) => {
          mapInstance.geoObjects.add(
            new ymaps.Placemark(
              [properties.lat, properties.lon],
              {
                balloonContent: `
                  <h4>${properties.name}</h4>
                  <div>${properties.category}</div>
                  `,
              },
              {
                preset: 'islands#circleIcon',
                iconLayout: 'default#image',
                iconImageHref: `/images/${infrasItem.type.toLowerCase()}.svg`,
                iconImageSize: [25, 25],
              },
            ),
          )
        })
      })
    },
    async initPrintMapWithoutInfrastructure() {
      const ymaps = this.yandexMaps
      const map = this.$refs.printMap
      const mapId = map.getAttribute('id')

      const mapInstance = new ymaps.Map(mapId, {
        center: [55.753994, 37.622093],
        zoom: 9,
      })

      const objectsCollection = new ymaps.GeoObjectCollection()
      const addressMarker = await this.getAddressMarker()
      if (addressMarker) {
        objectsCollection.add(addressMarker)
        mapInstance.geoObjects.add(objectsCollection)
      } else {
        this.showMaps = false
        return
      }

      mapInstance
        .setBounds(mapInstance.geoObjects.getBounds(), { checkZoomRange: true })
        .then(() => mapInstance.setZoom(16))
    },
    initMap(ymaps) {
      this.yandexMaps = ymaps

      if (
        ['print', 'standard'].includes(this.version) &&
        this.isInfrastructure
      ) {
        this.initMapWithInfrastructure()
        return
      }

      if (!this.isInfrastructure) {
        switch (this.version) {
          case 'standard':
            this.initMapWithInfrastructure()
            break
          case 'print':
            this.initPrintMapWithoutInfrastructure()
            break
        }
      }
    },
  },
  setup() {
    const showMaps = ref(true)
    return {
      yandexMaps: {},
      showMaps,
    }
  },
  mounted() {
    if (this.type !== 'yandex') return
    const url = `https://api-maps.yandex.ru/2.1/?lang=${this.activeLang}&apikey=${this.yakey}`
    ymaps.load(url).then(this.initMap)
  },
}
</script>
